/**
 * Init-config form primitives shared by the per-org editors and the
 * Plugins-page global-config dialogs.
 *
 * Both surfaces render `BaseInitAttr` fields the same way: each row is a
 * literal input (Input / Select / Boolean) or a `$secret` picker against
 * the org's defined secret fields. The two layers differ only in which
 * endpoints back the load/save mutations.
 */
import type { ReactNode } from 'react';

import type { FieldScopes, FieldSpec } from '@optum/api-client/types';

import { Input } from '../../../ui/finixt.js';
import { Select } from '../../../ui/Select.js';

const FRAMEWORK_FIELDS = new Set(['agent_id', 'capability_id']);

/** The baseline a field falls back to when this layer hasn't set it. */
export interface InheritedValue {
  value: unknown;
  /** Human label of the layer supplying the value, e.g. "global default". */
  label: string;
}

export function filterFrameworkFields(fields: FieldSpec[]): FieldSpec[] {
  return fields.filter((f) => !FRAMEWORK_FIELDS.has(f.name));
}

export function describeValue(v: unknown): string {
  if (v !== null && typeof v === 'object' && '$secret' in (v as Record<string, unknown>)) {
    return `$secret: ${String((v as Record<string, unknown>).$secret)}`;
  }
  if (v === null) return 'null';
  return String(v);
}

/** Pick the option list to render for a (possibly conditional) enum field. */
function resolveEnumOptions(spec: FieldSpec, siblings: Record<string, unknown>): unknown[] {
  if (spec.depends_on && spec.options_by) {
    const siblingValue = siblings[spec.depends_on];
    const subset = spec.options_by[String(siblingValue)];
    return subset ?? [];
  }
  return spec.options ?? [];
}

/**
 * When a discriminator field changes, drop any same-level dependent field
 * whose current value is no longer in the new sibling's allowed subset.
 * Returns a new record; never mutates the input.
 */
export function clearStaleDependents(
  fields: FieldSpec[],
  values: Record<string, unknown>,
  changedField: string,
): Record<string, unknown> {
  let next: Record<string, unknown> | null = null;
  for (const f of fields) {
    if (!f.depends_on || !f.options_by || f.depends_on !== changedField) continue;
    const siblingValue = values[f.depends_on];
    const allowed = f.options_by[String(siblingValue)] ?? [];
    const current = values[f.name];
    if (current == null) continue;
    if (!allowed.some((v) => String(v) === String(current))) {
      if (next === null) next = { ...values };
      delete next[f.name];
    }
  }
  return next ?? values;
}

/** Walk the stored config and trim string values to schema max_length. */
export function sanitizeAgainstSchema(
  config: Record<string, unknown>,
  initFields: FieldSpec[],
): { cleaned: Record<string, unknown>; trimmed: string[] } {
  const out: Record<string, unknown> = {};
  const trimmed: string[] = [];
  const fieldByName = new Map(initFields.map((f) => [f.name, f]));
  for (const [name, value] of Object.entries(config)) {
    const spec = fieldByName.get(name);
    if (
      spec &&
      spec.type === 'string' &&
      typeof value === 'string' &&
      spec.max_length !== null &&
      value.length > spec.max_length
    ) {
      out[name] = value.slice(0, spec.max_length);
      trimmed.push(name);
    } else {
      out[name] = value;
    }
  }
  return { cleaned: out, trimmed };
}

/** Build a map of fieldName -> validation message for any current violations. */
export function collectViolations(
  values: Record<string, unknown>,
  fields: FieldSpec[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const spec of fields) {
    if (FRAMEWORK_FIELDS.has(spec.name)) continue;
    const value = values[spec.name];
    // $secret refs are validated server-side at task time.
    if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      '$secret' in (value as Record<string, unknown>)
    ) {
      continue;
    }
    if (value === undefined || value === null) continue;
    if (spec.type === 'string' && typeof value === 'string') {
      if (spec.max_length !== null && value.length > spec.max_length) {
        out[spec.name] = `must be at most ${spec.max_length} character(s)`;
      } else if (spec.min_length !== null && value.length < spec.min_length) {
        out[spec.name] = `must be at least ${spec.min_length} character(s)`;
      }
    }
    if ((spec.type === 'integer' || spec.type === 'number') && typeof value === 'number') {
      if (spec.minimum !== null && value < spec.minimum) {
        out[spec.name] = `must be ≥ ${spec.minimum}`;
      } else if (spec.maximum !== null && value > spec.maximum) {
        out[spec.name] = `must be ≤ ${spec.maximum}`;
      }
    }
  }
  return out;
}

export function ConfigFieldRow({
  spec,
  value,
  siblings,
  violation,
  secretFieldKeys,
  inherited,
  footer,
  onChange,
}: {
  spec: FieldSpec;
  value: unknown;
  /** Same-level field values, used to resolve `spec.depends_on`. */
  siblings: Record<string, unknown>;
  violation?: string;
  secretFieldKeys: string[];
  /** Lower-layer value shown when this layer hasn't set the field. */
  inherited?: InheritedValue;
  /** Extra per-row content (e.g. scope toggles on the global editor). */
  footer?: ReactNode;
  onChange: (next: unknown) => void;
}) {
  const inheritedDisplay = inherited ? describeValue(inherited.value) : undefined;
  const showInherited = value === undefined && inherited !== undefined;
  const placeholder = showInherited && inheritedDisplay ? inheritedDisplay : '—';
  const isSecretRef =
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    '$secret' in (value as Record<string, unknown>);
  const mode: 'literal' | 'secret' = isSecretRef ? 'secret' : 'literal';
  const literalValue: unknown = isSecretRef || value === undefined || value === null ? '' : value;

  function setLiteral(v: string) {
    if (v === '' && spec.nullable) onChange(null);
    else if (v === '') onChange(undefined);
    else if (spec.type === 'integer') onChange(parseInt(v, 10));
    else if (spec.type === 'number') onChange(Number(v));
    else if (spec.type === 'boolean') onChange(v === 'true');
    else onChange(v);
  }

  function setSecretRef(key: string) {
    onChange({ $secret: key });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 7 }}>
        <span
          style={{ fontSize: 'var(--text-body-md)', fontWeight: 'var(--weight-medium)' }}
          className="mono"
        >
          {spec.name}
        </span>
        <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
          {spec.type}
        </span>
        {spec.required && (
          <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--danger)' }}>required</span>
        )}
        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 'var(--text-meta-lg)',
          }}
        >
          <button
            type="button"
            onClick={() => onChange(undefined)}
            style={{
              borderRadius: 'var(--radius-chip)',
              padding: '5px 7px',
              border: 'none',
              cursor: 'pointer',
              ...(mode === 'literal'
                ? { background: 'var(--fill-action)', color: 'var(--text-inverse)' }
                : { background: 'transparent', color: 'var(--text-muted)' }),
            }}
          >
            literal
          </button>
          <button
            type="button"
            onClick={() => onChange({ $secret: '' })}
            style={{
              borderRadius: 'var(--radius-chip)',
              padding: '5px 7px',
              border: 'none',
              cursor: 'pointer',
              ...(mode === 'secret'
                ? { background: 'var(--fill-action)', color: 'var(--text-inverse)' }
                : { background: 'transparent', color: 'var(--text-muted)' }),
            }}
          >
            $secret
          </button>
        </div>
      </div>
      {spec.description && (
        <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
          {spec.description}
        </p>
      )}
      {showInherited && (
        <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
          Inherited from {inherited!.label}: <code>{inheritedDisplay}</code> — set a value to
          override.
        </p>
      )}
      {mode === 'literal' && spec.type === 'enum' && spec.options && (
        <Select
          value={literalValue === '' ? '' : String(literalValue)}
          onChange={(v) => {
            setLiteral(v);
          }}
          options={[
            { value: '', label: placeholder },
            ...resolveEnumOptions(spec, siblings).map((opt) => ({
              value: String(opt),
              label: String(opt),
            })),
          ]}
        />
      )}
      {mode === 'literal' && spec.type === 'boolean' && (
        <Select
          value={literalValue === '' ? '' : String(literalValue)}
          onChange={(v) => {
            setLiteral(v);
          }}
          options={[
            { value: '', label: placeholder },
            { value: 'true', label: 'true' },
            { value: 'false', label: 'false' },
          ]}
        />
      )}
      {mode === 'literal' && spec.type !== 'boolean' && spec.type !== 'enum' && (
        <Input
          type={spec.type === 'integer' || spec.type === 'number' ? 'number' : 'text'}
          step={spec.type === 'integer' ? 1 : 'any'}
          value={literalValue === '' ? '' : String(literalValue)}
          onChange={(e) => setLiteral(e.target.value)}
          {...(showInherited && inheritedDisplay ? { placeholder: inheritedDisplay } : {})}
          {...(spec.min_length !== null ? { minLength: spec.min_length } : {})}
          {...(spec.max_length !== null ? { maxLength: spec.max_length } : {})}
          {...(spec.minimum !== null ? { min: spec.minimum } : {})}
          {...(spec.maximum !== null ? { max: spec.maximum } : {})}
        />
      )}
      {(spec.min_length !== null ||
        spec.max_length !== null ||
        spec.minimum !== null ||
        spec.maximum !== null) && (
        <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
          {[
            spec.min_length !== null || spec.max_length !== null
              ? `length ${spec.min_length ?? 0}..${spec.max_length ?? '∞'}`
              : '',
            spec.minimum !== null || spec.maximum !== null
              ? `range ${spec.minimum ?? '−∞'}..${spec.maximum ?? '∞'}`
              : '',
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
      {violation && (
        <p role="alert" style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--danger)' }}>
          {violation} (Save is disabled until fixed)
        </p>
      )}
      {mode === 'secret' && (
        <Select
          value={isSecretRef ? String((value as Record<string, unknown>)['$secret'] ?? '') : ''}
          onChange={(value) => setSecretRef(value)}
          options={[
            {
              value: '',
              label:
                secretFieldKeys.length === 0 ? 'No secret fields defined' : 'Pick a secret field',
            },
            ...secretFieldKeys.map((k) => ({ value: k, label: k })),
          ]}
        />
      )}
      {footer}
    </div>
  );
}

/** Per-field override-scope toggles shown on the global editors (Feature 25). */
/** The layers a config row may open beneath itself.
 *
 *  A global row may open `partner`; a partner row may not, because a layer
 *  does not gate itself (Feature 48).
 */
export type OverridableScope = 'partner' | 'organization' | 'user';

export function ScopeChips({
  scopes,
  showPartner = false,
  showUser,
  onToggle,
}: {
  scopes: FieldScopes[string] | undefined;
  showPartner?: boolean;
  showUser: boolean;
  onToggle: (scope: OverridableScope) => void;
}) {
  const chip = (scope: OverridableScope, label: string) => {
    const on = scopes?.[scope] === true;
    return (
      <button
        type="button"
        onClick={() => onToggle(scope)}
        aria-pressed={on}
        style={{
          borderRadius: 'var(--radius-chip)',
          padding: '5px 7px',
          cursor: 'pointer',
          ...(on
            ? {
                background: 'var(--fill-action)',
                color: 'var(--text-inverse)',
                border: '1px solid transparent',
              }
            : {
                background: 'transparent',
                color: 'var(--text-muted)',
                border: '1px solid var(--border-input)',
              }),
        }}
      >
        {label}
      </button>
    );
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 'var(--text-meta-lg)' }}>
      <span style={{ color: 'var(--text-muted)' }}>Overridable at:</span>
      {showPartner && chip('partner', 'Partner')}
      {chip('organization', 'Org')}
      {showUser && chip('user', 'User')}
    </div>
  );
}
